import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as ApplicationIcon,
  Assessment as ReportsIcon,
  AdminPanelSettings as AdminIcon,
  Person as ProfileIcon,
  Add as AddIcon,
  List as ListIcon,
  People as UsersIcon,
  Settings as SettingsIcon,
  LocationOn as LocationIcon,
  Map as MapIcon,
  Assignment as AssignmentIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Schedule as ScheduleIcon,
  InsertDriveFile as InsertDriveFileIcon,
  NewReleases as NewReleasesIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';

const DRAWER_WIDTH = 240;

const menuItems = [
  {
    text: 'Dashboard',
    icon: <DashboardIcon />,
    path: '/dashboard',
    roles: ['admin', 'l1_staff', 'dc', 'dpo', 'sdm', 'citizen', 'reve_o', 'ahvs_h', 'agri_h', 'horti_h', 'reve_i', 'ahvs_i', 'agri_i', 'horti_i', 'accounts'],
  },
  {
    text: 'Applications',
    icon: <ApplicationIcon />,
    roles: ['admin', 'l1_staff', 'dc', 'dpo', 'sdm', 'citizen', 'accounts'],
    children: [
      {
        text: 'New Applications',
        icon: <InsertDriveFileIcon />,
        path: '/applications/newapplications',
        roles: ['dpo'],
      },
      {
        text: 'Assigned to Me',
        icon: <AssignmentIcon />,
        path: '/applications/assigned',
        roles: ['dc', 'sdm', 'accounts', 'dpo'],
      },
      {
        text: 'All Applications',
        icon: <ListIcon />,
        path: '/applications',
        roles: ['admin', 'l1_staff', 'dc', 'dpo', 'sdm', 'accounts'],
      },
      {
        text: 'My Applications',
        icon: <ListIcon />,
        path: '/applications',
        roles: ['citizen'],
      },
      {
        text: 'Apply New Application',
        icon: <AddIcon />,
        path: '/applications/apply',
        roles: ['admin', 'l1_staff', 'citizen'],
      },
    ],
  },
  {
    text: 'Damage Inspection',
    icon: <ApplicationIcon />,
    roles: ['reve_o', 'ahvs_h', 'agri_h', 'horti_h'],
    children: [
      {
        text: 'New Requests',
        icon: <AssignmentIcon />,
        path: '/inspections/new-requests',
        roles: ['reve_o', 'ahvs_h', 'agri_h', 'horti_h'],
      },
      // {
      //   text: 'Pending Field Reports',
      //   icon: <ListIcon />,
      //   path: '/inspections/pending-reports',
      //   roles: ['reve_o', 'ahvs_h', 'agri_h', 'horti_h'],
      // },
      {
        text: 'Unverified Reports',
        icon: <NewReleasesIcon />,
        path: '/inspections/unverified-reports',
        roles: ['reve_o', 'ahvs_h', 'agri_h', 'horti_h'],
      },
      {
        text: 'Verified Reports',
        icon: <FactCheckIcon />,
        path: '/inspections/verified-reports',
        roles: ['reve_o', 'ahvs_h', 'agri_h', 'horti_h'],
      },
      {
        text: 'All Inspection Reports',
        icon: <ListIcon />,
        path: '/inspections/all-assigned-requests/head',
        roles: ['reve_o', 'ahvs_h', 'agri_h', 'horti_h'],
      }
    ],
  },
  {
    text: 'Field Inspection',
    icon: <ReportsIcon />,
    roles: ['reve_i', 'ahvs_i', 'agri_i', 'horti_i'],
    children: [
      {
        text: 'Pending Inspections',
        icon: <ListIcon />,
        path: '/inspections/pending-inspections',
        roles: ['reve_i', 'ahvs_i', 'agri_i', 'horti_i'],
      },
      {
        text: 'My Reports',
        icon: <ListIcon />,
        path: '/inspections/my-reports',
        roles: ['reve_i', 'ahvs_i', 'agri_i', 'horti_i'],
      }
    ],
  },
  {
    text: 'Reports',
    icon: <ReportsIcon />,
    path: '/reports',
    roles: ['admin', 'l1_staff', 'dc', 'dpo', 'sdm'],
  },
  {
    text: 'Geospatial Report',
    icon: <MapIcon />,
    path: '/geospatial-report',
    roles: ['admin', 'l1_staff', 'dc', 'dpo', 'sdm', 'reve_o', 'ahvs_h', 'agri_h', 'horti_h'],
  },
  {
    text: 'Profile',
    icon: <ProfileIcon />,
    path: '/profile',
    roles: ['admin', 'l1_staff', 'dc', 'dpo', 'citizen', 'inspector'],
  },
];

const adminMenuItems = [
  {
    text: 'Administration',
    icon: <AdminIcon />,
    roles: ['admin'],
    children: [
      {
        text: 'User Management',
        icon: <UsersIcon />,
        path: '/admin/users',
        roles: ['admin'],
      },
      {
        text: 'Role Management',
        icon: <SettingsIcon />,
        path: '/admin/roles',
        roles: ['admin'],
      },
      {
        text: 'Workflow Config',
        icon: <SettingsIcon />,
        path: '/admin/workflow',
        roles: ['admin'],
      },
      {
        text: 'District Management',
        icon: <LocationIcon />,
        path: '/admin/districts',
        roles: ['admin'],
      },
      {
        text: 'Audit Logs',
        icon: <SettingsIcon />,
        path: '/admin/audit',
        roles: ['admin'],
      },
    ],
  },
];

const Sidebar = ({ open, onClose, variant }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { full_name } = useSelector((state) => state.auth);
  const { hasRole, hasAnyRole, userRoleFullName, userDistrict, userSubdivision, userBlock } = useAuth();
  const [expandedItems, setExpandedItems] = useState({});

  const hasRoleAccess = (roles) => {
    if (!roles || roles.length === 0) return true;
    return hasAnyRole(roles);
  };

  const handleItemClick = (item) => {
    if (item.children) {
      setExpandedItems({
        ...expandedItems,
        [item.text]: !expandedItems[item.text],
      });
    } else if (item.path) {
      navigate(item.path);
      if (variant === 'temporary') {
        onClose();
      }
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const renderMenuItem = (item, isChild = false) => {
    if (!hasRoleAccess(item.roles)) {
      return null;
    }

    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding sx={{ pl: isChild ? 1 : 0 }}>
          <ListItemButton
            onClick={() => handleItemClick(item)}
            selected={item.path && isActive(item.path)}
            sx={{
              minHeight: 48,
              px: 1,
              borderRadius: 1,
              mx: 1,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: 1,
                justifyContent: 'center',
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              primaryTypographyProps={{
                fontSize: isChild ? '0.875rem' : '1rem',
                fontWeight: isChild ? 'normal' : 'medium',
              }}
            />
            {item.children && (
              expandedItems[item.text] ? <ExpandLessIcon /> : <ExpandMoreIcon />
            )}
          </ListItemButton>
        </ListItem>
        
        {item.children && (
          <Collapse in={expandedItems[item.text]} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => renderMenuItem(child, true))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Header */}
      <Box
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: 1,
          borderColor: 'divider',
          mt: 8, // Account for fixed header
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="primary">
          NC Ex-Gratia
        </Typography>
      </Box>

      {/* User Info */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary">
          Welcome back
        </Typography>
        <Typography variant="subtitle1" fontWeight="medium">
          {full_name || 'User'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {userRoleFullName?.toUpperCase() || 'ROLE'}
        </Typography>
        <Divider />
        <Typography variant="caption" color="text.secondary">
          {userDistrict ? `${userDistrict.toUpperCase()}` : '-'} | {userSubdivision ? `${userSubdivision.toUpperCase()}` : '-'} | {userBlock ? `${userBlock.toUpperCase()}` : '-'}
        </Typography>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ pt: 1 }}>
          {menuItems.map((item) => renderMenuItem(item))}
          
          {/* Admin Section */}
          {hasRole('admin') && (
            <>
              <Divider sx={{ my: 1 }} />
              {adminMenuItems.map((item) => renderMenuItem(item))}
            </>
          )}
        </List>
      </Box>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" align="center">
          © 2025 NC Ex-Gratia System
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          borderRight: 1,
          borderColor: 'divider',
        },
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default Sidebar;
